/**
 * Rezzaa Theme - Global JavaScript
 * Core utilities and theme functionality
 */

// Global theme namespace
window.Rezzaa = window.Rezzaa || {};

// Debounce utility
Rezzaa.debounce = function(fn, delay = 300) {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
};

// Throttle utility
Rezzaa.throttle = function(fn, limit = 100) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

// Format money helper
Rezzaa.formatMoney = function(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = (cents / 100).toFixed(2);
  const formatString = format || window.Shopify?.moneyFormat || '${{amount}}';
  return formatString.replace(/\{\{amount\}\}/g, value);
};

// Fetch wrapper with error handling
Rezzaa.fetch = async function(url, options = {}) {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};

// Publish/Subscribe for component communication
Rezzaa.pubsub = {
  events: {},

  subscribe(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);

    return () => {
      this.events[event] = this.events[event].filter(cb => cb !== callback);
    };
  },

  publish(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(data));
  }
};

// Trap focus for modals/drawers
Rezzaa.trapFocus = function(container, focusElement) {
  const focusableElements = container.querySelectorAll(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      }
    }
  }

  container.addEventListener('keydown', handleKeydown);

  if (focusElement) {
    focusElement.focus();
  } else if (firstFocusable) {
    firstFocusable.focus();
  }

  return () => container.removeEventListener('keydown', handleKeydown);
};

// Scroll lock for modals
Rezzaa.scrollLock = {
  scrollPosition: 0,

  lock() {
    this.scrollPosition = window.pageYOffset;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = '100%';
  },

  unlock() {
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    window.scrollTo(0, this.scrollPosition);
  }
};

// Intersection Observer for lazy animations
Rezzaa.observe = function(elements, callback, options = {}) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback(entry.target);
        if (options.once !== false) {
          observer.unobserve(entry.target);
        }
      }
    });
  }, {
    root: options.root || null,
    rootMargin: options.rootMargin || '0px',
    threshold: options.threshold || 0.1
  });

  elements.forEach(el => observer.observe(el));

  return observer;
};

// Initialize scroll-triggered animations
document.addEventListener('DOMContentLoaded', function() {
  // Reveal animations on scroll
  const revealElements = document.querySelectorAll('[data-reveal]');
  if (revealElements.length) {
    Rezzaa.observe(revealElements, (el) => {
      el.classList.add('revealed');
    });
  }

  // Lazy load images
  const lazyImages = document.querySelectorAll('img[loading="lazy"]');
  if ('loading' in HTMLImageElement.prototype) {
    // Browser supports native lazy loading
  } else {
    // Fallback for older browsers
    Rezzaa.observe(lazyImages, (img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
    });
  }
});

// Accessibility: Reduce motion preference
Rezzaa.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Accessibility: Focus visible utility
document.addEventListener('keydown', function(e) {
  if (e.key === 'Tab') {
    document.body.classList.add('user-is-tabbing');
  }
});

document.addEventListener('mousedown', function() {
  document.body.classList.remove('user-is-tabbing');
});

// Console branding
console.log(
  '%c REZZAA THEME ',
  'background: #00D9FF; color: #0A0A0A; font-weight: bold; padding: 4px 8px; font-family: monospace;'
);
